import { convertToUtc } from "@gigpro/utils/datetime";
import {
  getMessagingChatLocationUnreadCountRetrieveQueryOptions,
  getStafferGigApplicationsUnpaidListQueryOptions,
  getStafferGigApplicationsUnratedListQueryOptions,
  getStafferGigsV2ListWithTransformQueryOptions,
  type ForManagerLocationDetail,
} from "@sidegigllc/api/client";
import { selectStafferGigs, type GigsQueryPayload } from "api/queries/stafferGigs";
import { endOfToday, startOfMinute, startOfToday, startOfTomorrow, subDays } from "date-fns";
import type { WarmableQuery } from "routing/warmQueries";

type QueriesInput = { location: ForManagerLocationDetail; now: Date };

const commonGigParams: GigsQueryPayload = {
  page: 1,
  page_size: 5,
  ordering: "start_time",
  statuses: ["POSTED", "UNAPPROVED", "UNFILLED", "CONFIRMED"],
};

const gigsPreviewQuery = (
  location: ForManagerLocationDetail,
  range: { start_time__gte: Date; start_time__lte?: Date },
) =>
  getStafferGigsV2ListWithTransformQueryOptions(
    { ...commonGigParams, location_id: location.id, ...range },
    { query: { select: selectStafferGigs } },
  );

export const todayGigsQuery = ({ location }: { location: ForManagerLocationDetail }) =>
  gigsPreviewQuery(location, {
    start_time__gte: convertToUtc(startOfToday(), location.timezone),
    start_time__lte: convertToUtc(endOfToday(), location.timezone),
  });

export const upcomingGigsQuery = ({ location }: { location: ForManagerLocationDetail }) =>
  gigsPreviewQuery(location, { start_time__gte: convertToUtc(startOfTomorrow(), location.timezone) });

const gigEndTimeGte = (now: Date) => startOfMinute(subDays(now, 14));

export const unpaidQuery = ({ now }: { now: Date }) =>
  getStafferGigApplicationsUnpaidListQueryOptions({
    page: 1,
    page_size: 3,
    ordering: "gig__end_time",
    gig__end_time__gte: gigEndTimeGte(now),
  });

export const unratedQuery = ({ now }: { now: Date }) =>
  getStafferGigApplicationsUnratedListQueryOptions({
    ordering: "gig__end_time",
    page: 1,
    page_size: 20,
    gig__end_time__gte: gigEndTimeGte(now),
  });

export const unreadMessagesQuery = ({ location }: { location: ForManagerLocationDetail }) =>
  getMessagingChatLocationUnreadCountRetrieveQueryOptions(location.id);

export const queries = (input: QueriesInput): Array<WarmableQuery> => [
  todayGigsQuery(input),
  unpaidQuery(input),
  unratedQuery(input),
  unreadMessagesQuery(input),
];
